import {useEffect, useRef, useState} from 'react';

/**
 * Selected-row state for a master-detail roster; `null` when the roster is empty.
 * `preferredIndex` is applied once, the first time the roster has rows — later changes never move the selection.
 */
export function useRosterSelection(count: number, preferredIndex: number | null = null) {
  const [selected, setSelected] = useState(0);
  const selectedIndex = count === 0 ? null : Math.min(selected, count - 1);
  const hasAppliedPreferredRef = useRef(false);

  useEffect(() => {
    if (count === 0 || hasAppliedPreferredRef.current) return;
    hasAppliedPreferredRef.current = true;
    if (preferredIndex !== null) setSelected(preferredIndex);
  }, [count, preferredIndex]);

  // Call before appending: the current length is the new row's index.
  const selectAfterAdd = () => setSelected(count);

  // The row that slides into the removed slot (or the new last row) takes over the selection.
  const adjustAfterRemove = (removedIndex: number) =>
    setSelected(prev => (removedIndex < prev ? prev - 1 : prev));

  return {selectedIndex, select: setSelected, selectAfterAdd, adjustAfterRemove};
}
